<template>
  <dropdown title="Priorities">
    <base-ifp-filter
      label="Priority"
      clearable
      @clear-clicked="clearSelectedIDs()"
    >
      <template #value-display>
        <template v-if="hasOneSelectedPriority">
          <priority-chicklet :priority="selectedPriorities[0]" hide-icon />
        </template>
        <template v-else-if="hasSelectedPriorities">
          <priority-chicklet 
            v-for="p in selectedPriorities" 
            :key="p.id"
            :priority="p"
            icon-only
            class="ifp-filter-priorities__multi-chicklet"
          />
        </template>
      </template>
    </base-ifp-filter>
    <template #content>
      <div>
        <ul>
          <li v-for="priority in priorityOptions" :key="priority.id" @click="handleSelection(priority.id)">
            <span :class="{ 'selected' : isSelected(priority.id) }">
              <priority-chicklet :priority="priority" />
            </span>
          </li>
        </ul>
      </div>
    </template>
  </dropdown>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { debounce } from 'lodash-es'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */
import { IPriorityLevelForSelection } from '@/api/types/response.types'

/* API */

export default defineComponent({
  name: 'IfpFilterPriorities',

  props: {
    selectedIds: {
      type: Array as () => number[],
      required: true,
    },
    priorityOptions: {
      type: Array as () => IPriorityLevelForSelection[],
      required: true,
    },
  },

  emits: ['selected-ids-updated'],

  data() {
    return {
      internalSelectedIDs: [] as number[],
      debounceEmitSelectedIDs: null as any,
      emitDebounceTimeForMultipleSelectionsInMS: 500,
    }
  },
  computed: {
    hasSelectedPriorities(): boolean {
      if (this.selectedIds.length === 0) {
        return false
      }

      return this.priorityOptions.some(p => this.selectedIds.includes(p.id))
    },
    hasOneSelectedPriority(): boolean {
      return this.selectedPriorities.length === 1
    },
    selectedPriorities(): IPriorityLevelForSelection[] {
      return this.priorityOptions.filter(p => this.selectedIds.includes(p.id))
    },
  },
  watch: {
    selectedIds: {
      deep: 1,
      immediate: true,
      handler(val) {
        this.internalSelectedIDs = val
      },
    },
  },
  created() {
    this.debounceEmitSelectedIDs = debounce(
      this._emitSelectedIDs,
      this.emitDebounceTimeForMultipleSelectionsInMS,
    )
  },
  methods: {
    isSelected(id: number) {
      return this.internalSelectedIDs.includes(id)
    },
    handleSelection(id: number) {
      if (this.internalSelectedIDs.includes(id)) {
        this._updateSelectedIDs(this.internalSelectedIDs.filter(i => i !== id))
      } else {
        this._updateSelectedIDs([...this.internalSelectedIDs, id])
      }
    },
    clearSelectedIDs() {
      /* Circumvent debounce and emit immediately */
      this._updateSelectedIDs([], true)
    },
    _updateSelectedIDs(ids: number[], emitImmediately?: boolean) {
      // Set this so the UI can instantly react to the updated IDs rather than waiting
      // for syncing on the selectedIds prop
      this.internalSelectedIDs = ids
      if (emitImmediately) {
        this._emitSelectedIDs()
      } else {
        this.debounceEmitSelectedIDs()
      }
    },
    _emitSelectedIDs() {
      this.$emit('selected-ids-updated', this.internalSelectedIDs)
    },
  },
})
</script>
<style lang="scss">
.ifp-filter-priorities {
  &__multi-chicklet {
    margin-right: 4px;
    width: 26px;
    padding: 0;
  }
}
</style>
